import React, { useMemo, useState } from 'react';
import Modal from 'src/shared/components/modal/Modal';
import { Loader } from 'src/shared/components/loader/Loader';
import DataTable from 'src/shared/components/data-table/DataTable';
import { DataTableColumns, DataTableState, TableRowData } from 'src/shared/components/data-table/dataTableTypes';
import { Badge } from 'src/shared/components/ui/badge';
import { SpeciesImage } from 'src/app/portfolio/components/species-image/SpeciesImage';
import IUCNStatusChip from 'src/shared/components/iucn-status-chip';
import { IUCNStatusCode } from 'src/shared/helpers/iucnStatusHelper';
import { getConfidenceDetail } from 'src/shared/helpers/speciesConfidence';
import SpeciesInfoModal from './SpeciesInfoModal';
import { ThreatenedSpecies } from '../../state/api/siteThreatenedSpeciesQuery';
import { useGetSiteThreatenedSpeciesQuery } from '../../state/api/siteNatureRiskEngineSlice';

type Props = {
    onClose: () => void;
    organisationBusinessCentralId: string;
    siteId: string;
    title?: string;
    subtitle?: string;
};

const DEFAULT_TITLE = 'Threatened species';
const DEFAULT_SUBTITLE = 'IUCN Red List species predicted for this site';

// A site's Red List set is bounded (low hundreds) — one page holds it all, then
// the DataTable paginates client-side.
const MODAL_PAGE_SIZE = 1000;

// Rows are clickable (open the species info modal), so every cell shows a pointer.
const tableColumns: DataTableColumns = [
    { columnId: 'speciesName', title: 'Species name', width: '55%', bodyCellClassName: 'cursor-pointer' },
    {
        columnId: 'iucnRedListStatus',
        title: 'IUCN Red List status',
        width: '30%',
        titleSlug: 'species-risk-site-iucn-status',
        bodyCellClassName: 'cursor-pointer',
    },
    {
        columnId: 'confidence',
        title: 'Prediction Confidence',
        width: '15%',
        titleSlug: 'species-risk-site-confidence',
        bodyCellClassName: 'cursor-pointer',
    },
];

/**
 * "View all" for the site-nature-risk Characteristics page — the full Red List
 * prediction for a site, sourced entirely from the engine (siteId + org id, the
 * new dynamic approach). The NatureMetrics-samples / uploaded-data comparison
 * lives only in the portfolio's `SiteSpeciesRiskModal`, which is left untouched.
 */
const SiteThreatenedSpeciesModal = ({
    onClose,
    organisationBusinessCentralId,
    siteId,
    title = DEFAULT_TITLE,
    subtitle = DEFAULT_SUBTITLE,
}: Props) => {
    const [tableState, setTableState] = useState<Partial<DataTableState>>({ fixedHeader: true, rowsPerPage: 10 });
    const [selectedSpecies, setSelectedSpecies] = useState<ThreatenedSpecies | null>(null);

    const { data: engineData, isFetching } = useGetSiteThreatenedSpeciesQuery(
        { organisationBusinessCentralId, siteId, page: 1, pageSize: MODAL_PAGE_SIZE },
        { skip: !organisationBusinessCentralId || !siteId }
    );

    // Each row's first cell is keyed by scientific name (see tableData), so a row
    // click resolves back to its species without threading an extra id column.
    const handleRowSelect = (rowData: TableRowData) => {
        const firstCell = rowData[0];
        const scientificName = React.isValidElement(firstCell) && typeof firstCell.key === 'string' ? firstCell.key : null;
        const species = (engineData?.siteThreatenedSpecies?.species.items ?? []).find(s => s.scientificName === scientificName);
        if (species) {
            setSelectedSpecies(species);
        }
    };

    // Derive the rows during render — no effect/extra render, and the DataTable's
    // own pagination state stays in `tableState`.
    const tableData: DataTableState['data'] = useMemo(() => {
        const species = engineData?.siteThreatenedSpecies?.species.items ?? [];
        return species.map(item => {
            const confidence = getConfidenceDetail(item.confidence);
            return [
                <div className='flex gap-3' key={item.scientificName}>
                    <div className='flex-shrink-0'>
                        <SpeciesImage
                            scientificName={item.scientificName}
                            alt={item.commonName || item.scientificName}
                            width='84px'
                            height='47px'
                        />
                    </div>
                    <div className='flex flex-col justify-center'>
                        <div className='text-sm'>{item.commonName}</div>
                        <div className='text-xs text-grey-80'>{item.scientificName}</div>
                    </div>
                </div>,
                <IUCNStatusChip
                    key={`${item.scientificName}-iucn`}
                    status={item.iucnCategory.toUpperCase() as IUCNStatusCode}
                    className='text-xs'
                />,
                <Badge key={`${item.scientificName}-confidence`} className={confidence.colorClass}>
                    {confidence.label}
                </Badge>,
            ];
        });
    }, [engineData]);

    return (
        <>
            <Modal dismissOnClickOutside={true} classNames={{ body: 'w-[800px] h-[600px] p-6' }} onClose={onClose}>
                {isFetching ? (
                    <div className='flex items-center justify-center h-full'>
                        <Loader />
                    </div>
                ) : (
                    <div className='flex flex-col gap-4 h-full' data-testid='site-threatened-species-modal'>
                        <div className='flex flex-col gap-1'>
                            <div className='text-3xl text-primary'>{title}</div>
                            <div className='text-base text-grey-80'>{subtitle}</div>
                        </div>
                        <div className='border-t border-grey-20 h-[1px]'>&nbsp;</div>
                        <div className='flex flex-col gap-0 pb-[80px]'>
                            <DataTable
                                state={{ ...tableState, data: tableData }}
                                columns={tableColumns}
                                onStateChange={setTableState}
                                onRowSelect={handleRowSelect}
                                emptyStateMessage='There are no IUCN Red List species predicted to be present at this site'
                                className='overflow-visible'
                                tableClassName='h-full'
                            />
                        </div>
                    </div>
                )}
            </Modal>

            {selectedSpecies && (
                <SpeciesInfoModal
                    onClose={() => setSelectedSpecies(null)}
                    scientificName={selectedSpecies.scientificName}
                    commonName={selectedSpecies.commonName}
                    iucnCategory={selectedSpecies.iucnCategory}
                    confidence={selectedSpecies.confidence}
                />
            )}
        </>
    );
};

export default SiteThreatenedSpeciesModal;
